import type { FC, MouseEvent, ReactNode } from 'react'
import { useState } from 'react'

import type { AssetType, WhiteLabel } from '@skytech/types'
import { AssetFallbackIcon, Card, Popup, StyledText, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { inStockStatusStyle } from '@skytech/utils'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'
import InfoOutlineIcon from 'mdi-react/InfoOutlineIcon'
import MinusIcon from 'mdi-react/MinusIcon'
import PlusIcon from 'mdi-react/PlusIcon'
import { twJoin, twMerge } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import { parseInStock } from '@/components/ecom-policy/policy/helpers'
import { ProductPriceDisplay } from '@/components/ecom-policy/policy/policy-products/common/product-price/ProdutPriceDisplay'
import type { GrossSalaryTaxBracket, Product } from '@/components/ecom-policy/types'
import { Highlight } from '@/components/Highlight'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { selectedStyleByWhiteLabel } from '@/components/util/style-constant'
import { useIsThreeStepIT } from '@/hooks/user'

import { ActionCardButton } from './ActionCardButton'
import type { ProductInfoProps } from './ProductInfo'
import { ProductInfo } from './ProductInfo'

export type ProductCardProps = {
  children?: ReactNode // Product price information
  isSingle: boolean
  isSelected: boolean
  productTitle: {
    productName: string
    productQuerySearch: string
  }
  productImg: { src?: string; assetType?: AssetType }
  productPrice: {
    employeePrice: number
    productPrice: number
    isFromPriceTextVisible: boolean
    grossSalaryTaxBracket: GrossSalaryTaxBracket
    isProductPriceVisible: boolean
  }
  productInStock: string
  productQuantity: {
    isQuantitySelectionAvailable: boolean
    quantity: number
    isQuantityPlusDisabled: boolean
    onPlusClick: (event: MouseEvent<HTMLElement>) => void
    onMinusClick: (event: MouseEvent<HTMLElement>) => void
  }
  onCardSelect?: (event: MouseEvent<HTMLElement>) => void
  className?: string
  isSelectedOnAnotherPolicy?: boolean
  isSelectionDisabled: boolean
  product: Product
  showNextPage?: () => void
  setMoreInfoProduct?: (product: ProductInfoProps) => void
}

export const ProductCardWithInfo: FC<ProductCardProps> = ({
  children,
  isSingle = false,
  isSelected,
  productTitle,
  productImg,
  productPrice,
  productInStock,
  productQuantity,
  onCardSelect,
  className,
  isSelectedOnAnotherPolicy = false,
  isSelectionDisabled = false,
  product,
  showNextPage,
  setMoreInfoProduct,
}) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()
  const isThreeStepIT = useIsThreeStepIT()
  const [showMoreInfo, setShowMoreInfo] = useState(false)

  const onMoreInfoClick = (e: MouseEvent<HTMLElement>) => {
    if (isSingle && showNextPage && setMoreInfoProduct) {
      showNextPage()
      setMoreInfoProduct({
        product,
        isSingle,
      })
      return
    }

    if (isSingle && !showMoreInfo) {
      setShowMoreInfo(true)
    } else if (!isSingle && onCardSelect) {
      onCardSelect(e)
    }
  }

  return (
    <>
      <Card
        noMargin
        className={twMerge(
          'shadow-basic relative flex h-full w-full flex-col items-center justify-between overflow-hidden border-2 border-solid !px-4 !py-2.5 transition duration-200 ease-out',
          isSelected
            ? selectedStyleByWhiteLabel[whiteLabel]?.border ||
                selectedStyleByWhiteLabel.skytech!.border
            : 'border-transparent',
          !isSelectedOnAnotherPolicy && 'hover:shadow-basic-black focus:shadow-basic-black',
          className
        )}
      >
        {/* Checkbox icon */}
        {!isSingle && isSelected && (
          <div
            className={twJoin(
              'absolute right-2 top-2.5 z-20 block',
              selectedStyleByWhiteLabel[whiteLabel]?.fill || selectedStyleByWhiteLabel.skytech!.fill
            )}
          >
            <CheckboxMarkedCircleOutlineIcon
              size={20}
              color={twJoin(
                selectedStyleByWhiteLabel[whiteLabel]?.fill ||
                  selectedStyleByWhiteLabel.skytech!.fill
              )}
            />
          </div>
        )}

        {/* Info icon */}
        {isSingle && (
          <div className="absolute right-0 top-0 z-[19]">
            <Button
              isIconRight
              id={`more-info-${productTitle.productName}`}
              variant="tertiary"
              className={twJoin(
                'm-0 h-8 pl-0 pr-2 pt-2',
                isThreeStepIT ? 'fill-threeStepIT-neutral' : 'fill-sky-600'
              )}
              Icon={
                <InfoOutlineIcon
                  color={twJoin(isThreeStepIT ? 'fill-threeStepIT-neutral' : 'fill-sky-600')}
                  size={32}
                />
              }
              onClick={onMoreInfoClick}
            >
              <Tooltip
                place="top"
                anchorId={`more-info-${productTitle.productName}`}
                content={translate('More information')}
                size="base"
                style={{ zIndex: 100 }}
              />
            </Button>
          </div>
        )}

        <div className="flex h-full w-full flex-col items-center">
          {/* Product image */}
          <button
            className="z-10 m-0 mb-4 flex h-28 w-full cursor-pointer appearance-none items-center justify-center overflow-hidden border-none bg-transparent p-0 transition hover:scale-125"
            onClick={e => {
              e.preventDefault()
              onMoreInfoClick(e)
            }}
          >
            <ImageWithIconFallback
              src={productImg.src || ''}
              alt="product"
              className="mx-auto block h-full max-w-full transition"
              fallbackIcon={
                <AssetFallbackIcon
                  assetType={productImg.assetType || 'OTHER'}
                  size={6}
                  className="!h-full"
                />
              }
            />
          </button>

          <div className="w-full text-left">
            {/* Product name */}
            <button
              id={`product-${productTitle.productName}`}
              onClick={e => {
                e.preventDefault()
                onMoreInfoClick(e)
              }}
              type="button"
              className="m-0 w-full cursor-pointer appearance-none border-none bg-transparent p-0 text-left transition"
            >
              <StyledText
                colorMode="black"
                size="regular-plus"
                fontWeight="semibold"
                className="line-clamp-3 text-ellipsis !text-xs transition sm:!text-sm md:!text-base"
              >
                <Highlight
                  pattern={productTitle.productQuerySearch || ''}
                  text={productTitle.productName}
                />
              </StyledText>
            </button>
          </div>
        </div>
        <div className="mt-0 w-full sm:mt-1 md:mt-1.5">
          {/* Product price information */}
          {children ? (
            children
          ) : (
            <ProductPriceDisplay
              isSelectionDisabled={isSelectionDisabled}
              grossSalaryTaxBracket={productPrice.grossSalaryTaxBracket}
              employeePrice={productPrice.employeePrice}
              productPrice={productPrice.productPrice}
              isProductPriceVisible={productPrice.isProductPriceVisible}
              isFromPriceTextVisible={productPrice.isFromPriceTextVisible ?? false}
              isSingleProductGroup={isSingle}
              displayMode={productPrice.grossSalaryTaxBracket ? 'grossSalaryScheme' : 'default'}
            />
          )}
          {/* Product stock information */}
          <div className="mt-1 flex w-full flex-row items-baseline gap-1 text-left leading-normal sm:mt-1.5 md:mt-2 md:gap-1.5">
            {!productInStock || parseInStock(productInStock) === '0' ? (
              <>
                <span
                  className={twMerge(
                    'flex size-2.5 flex-shrink-0 rounded-lg max-sm:size-2',
                    whiteLabel === 'threeStepIT' ? 'bg-threeStepIT-neutral' : 'bg-blue'
                  )}
                />
                <StyledText size="regular" colorMode="gray" className="max-sm:!text-xs">
                  <Translation id="Available in stock" />
                </StyledText>
              </>
            ) : (
              <>
                <span
                  className={twMerge(
                    'flex size-2.5 flex-shrink-0 rounded-lg max-sm:size-2',
                    inStockStatusStyle(productInStock, whiteLabel)
                  )}
                />
                <StyledText size="regular" colorMode="gray" className="max-sm:!text-xs">
                  {productInStock} <Translation id="in stock" />
                </StyledText>
              </>
            )}
          </div>

          <ActionCardButton
            className="mt-1.5 sm:mt-2"
            isSingle={isSingle}
            isSelected={isSelected}
            actionDisabled={isSelectedOnAnotherPolicy || isSelectionDisabled}
            onCardSelect={onCardSelect}
          />
        </div>

        {isSelectedOnAnotherPolicy && (
          <div className="absolute left-0 top-1/2 z-30 flex h-full w-full -translate-y-1/2 items-center justify-center rounded-lg bg-slate-50/50 text-white">
            <div
              className={twJoin(
                'flex h-fit w-full items-center justify-center text-wrap rounded-lg p-2 text-center text-sm text-white sm:text-base',
                selectedStyleByWhiteLabel[whiteLabel]?.bg || selectedStyleByWhiteLabel.skytech!.bg
              )}
            >
              <Translation id="Product already chosen" />
            </div>
          </div>
        )}

        {!isSelectionDisabled && productQuantity.isQuantitySelectionAvailable && (
          <PlusMinusButtons
            productQuantity={productQuantity}
            whiteLabel={whiteLabel}
            plusMinusButtonsDisabled={isSelectionDisabled}
          />
        )}
      </Card>

      {/* Product information */}
      {isSingle && (
        <Popup
          variant="wide"
          ariaLabel={translate('Modal with product description')}
          isOpen={showMoreInfo}
          onClose={() => setShowMoreInfo(false)}
        >
          <ProductInfo
            product={product}
            isSingle={isSingle}
            onClose={() => setShowMoreInfo(false)}
            selectBtn={
              <ActionCardButton
                className="w-full sm:w-fit"
                size="regular"
                isSingle={isSingle}
                isSelected={isSelected}
                actionDisabled={isSelectedOnAnotherPolicy || isSelectionDisabled}
                onCardSelect={onCardSelect}
              />
            }
          />
        </Popup>
      )}
    </>
  )
}

const PlusMinusButtons: FC<
  Pick<ProductCardProps, 'productQuantity'> & {
    whiteLabel: WhiteLabel
    plusMinusButtonsDisabled: boolean
  }
> = ({ productQuantity, whiteLabel, plusMinusButtonsDisabled }) => {
  const isThreeStepIT = useIsThreeStepIT()

  return (
    <div className="absolute right-1.5 top-8 z-20 flex flex-col items-center justify-center text-xs sm:text-base">
      <Button
        variant="tertiary"
        className={twJoin(
          'm-0 bg-transparent p-0',
          !isThreeStepIT &&
            `hover:${selectedStyleByWhiteLabel[whiteLabel || 'skytech']?.text} focus:${selectedStyleByWhiteLabel[whiteLabel || 'skytech']?.text} active:${selectedStyleByWhiteLabel[whiteLabel || 'skytech']?.text}`
        )}
        size="small"
        Icon={<PlusIcon className="mr-0 w-5 sm:w-[1.4rem]" />}
        disabled={productQuantity.isQuantityPlusDisabled}
        onClick={e => productQuantity.onPlusClick(e)}
      />
      <div className={twJoin(selectedStyleByWhiteLabel[whiteLabel || 'skytech']?.text)}>
        {productQuantity.quantity}
      </div>
      <Button
        variant="tertiary"
        className={twJoin(
          'm-0 bg-transparent p-0',
          !isThreeStepIT &&
            `hover:${selectedStyleByWhiteLabel[whiteLabel]?.text || selectedStyleByWhiteLabel.skytech!.text} focus:${selectedStyleByWhiteLabel[whiteLabel || 'skytech']?.text} active:${selectedStyleByWhiteLabel[whiteLabel || 'skytech']?.text}`
        )}
        disabled={plusMinusButtonsDisabled}
        size="small"
        Icon={<MinusIcon className="mr-0 w-5 sm:w-[1.4rem]" />}
        onClick={e => productQuantity.onMinusClick(e)}
      />
    </div>
  )
}
